document.addEventListener('DOMContentLoaded', () => {
  const container = document.querySelector('[data-visual-search]');
  if (!container) return;

  // ─── Popup modal: open/close with smooth transitions ───
  const modal      = document.querySelector('[data-vs-modal]');
  const openButtons  = document.querySelectorAll('[data-vs-open]');
  const closeTargets = modal ? modal.querySelectorAll('[data-vs-close]') : [];
  const dialog       = modal?.querySelector('.vs-modal__dialog');
  let closeTimer = null;
  let lastFocused = null;

  const openModal = () => {
    if (!modal) return;
    if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
    loadCategories(); // lazy-load the live category list on first open
    lastFocused = document.activeElement;
    modal.hidden = false;
    document.documentElement.classList.add('vs-modal-open');
    document.body.classList.add('vs-modal-open');
    openButtons.forEach((b) => b.setAttribute('aria-expanded', 'true'));
    // Force a reflow so the transition runs from the hidden start state.
    void modal.offsetWidth;
    modal.classList.add('is-open');
    (modal.querySelector('.vs-modal__close') || dialog)?.focus?.();
  };

  const closeModal = () => {
    if (!modal || modal.hidden) return;
    modal.classList.remove('is-open');
    openButtons.forEach((b) => b.setAttribute('aria-expanded', 'false'));
    document.documentElement.classList.remove('vs-modal-open');
    document.body.classList.remove('vs-modal-open');
    const finish = () => {
      if (modal.classList.contains('is-open')) return; // reopened mid-close
      modal.hidden = true;
      closeTimer = null;
    };
    // Wait for the fade-out to finish, with a timeout fallback.
    const onEnd = (e) => {
      if (e.target !== dialog) return;
      dialog.removeEventListener('transitionend', onEnd);
      if (closeTimer) { clearTimeout(closeTimer); }
      finish();
    };
    if (dialog) dialog.addEventListener('transitionend', onEnd);
    closeTimer = setTimeout(finish, 400);
    lastFocused?.focus?.();
  };

  openButtons.forEach((btn) => btn.addEventListener('click', openModal));
  closeTargets.forEach((el) => el.addEventListener('click', closeModal));
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal && !modal.hidden) closeModal();
  });

  // ─── Keyboard: keep Tab inside the open dialog (WCAG 2.1.2 / 2.4.3) ───
  // Inert whenever the modal is hidden. The native file picker is an OS dialog
  // outside the document, so it is unaffected. Focus restore on close is
  // already handled by `lastFocused` above.
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  // Reachable controls only: a [hidden] subtree reports zero size, which keeps
  // the preview / error / empty controls out of the cycle while they're closed.
  const focusableInDialog = () =>
    Array.from(dialog ? dialog.querySelectorAll(FOCUSABLE_SELECTOR) : []).filter(
      (el) => el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement,
    );

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab' || !modal || modal.hidden || !dialog) return;
    const items = focusableInDialog();
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    const outside = !dialog.contains(active);
    if (e.shiftKey && (active === first || outside)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (active === last || outside)) {
      e.preventDefault();
      first.focus();
    }
  });

  // Some live themes/apps disable vsConsole.log in production. Pull a pristine
  // console from a detached iframe so our [visual-search] debug logs always show.
  const vsConsole = (() => {
    try {
      const frame = document.createElement('iframe');
      frame.style.display = 'none';
      (document.body || document.documentElement).appendChild(frame);
      const native = frame.contentWindow.console;
      if (native && typeof native.log === 'function') return native;
    } catch (err) { /* fall back to the page console */ }
    return window.console;
  })();

  const UPLOAD_URL_ENDPOINT = container.dataset.uploadEndpoint || 'https://szqyztj9m2.execute-api.ap-south-1.amazonaws.com/get-upload-url';
  const AI_SEARCH_ENDPOINT  = container.dataset.searchEndpoint || 'https://d23rcqy3ievn7u.cloudfront.net/search-by-url';
  const AI_ASSET_BASE       = new URL(AI_SEARCH_ENDPOINT).origin;
  const CATEGORIES_ENDPOINT = container.dataset.categoriesEndpoint || `${AI_ASSET_BASE}/categories`;
  const SEARCH_URL          = (window.theme && theme.routes && theme.routes.search_url) || container.dataset.searchUrl || '/search';
  // Locale-aware storefront root (e.g. '' or '/en-in') for building product URLs.
  const ROOT_URL            = ((window.theme && theme.routes && theme.routes.root_url) || '/').replace(/\/$/, '');

  const MAX_FILE_BYTES      = 30 * 1024 * 1024;
  // Only the formats the AI backend accepts. Anything else is rejected up front.
  const ALLOWED_MIME_TYPES  = ['image/jpeg', 'image/png', 'image/webp'];
  // No results-count control: request a high cap so the backend returns all matches.
  const TOP_K               = 50;
  const INIT_TIMEOUT_MS     = 15000;
  const UPLOAD_STALL_MS     = 30000;
  const AI_TIMEOUT_MS       = 65000;
  const COLD_START_HINT_MS  = 5000;
  const RETRY_BACKOFF_MS    = 1500;

  const fileInput       = container.querySelector('[data-vs-file-input]');
  const uploadText      = container.querySelector('[data-vs-upload-text]');
  const preview         = container.querySelector('[data-vs-preview]');
  const previewImg      = container.querySelector('[data-vs-preview-img]');
  const removeBtn       = container.querySelector('[data-vs-remove]');
  const searchBtn       = container.querySelector('[data-vs-search]');
  const loadingEl       = container.querySelector('[data-vs-loading]');
  const loadingMessage  = container.querySelector('[data-vs-loading-message]');
  const progressEl      = container.querySelector('[data-vs-progress]');
  const progressBar     = container.querySelector('[data-vs-progress-bar]');
  const errorEl         = container.querySelector('[data-vs-error]');
  const errorMessageEl  = container.querySelector('[data-vs-error-message]');
  const errorRetryBtn   = container.querySelector('[data-vs-error-retry]');
  const errorDismissBtn = container.querySelector('[data-vs-error-dismiss]');
  const emptyEl         = container.querySelector('[data-vs-empty]');
  const emptyMessageEl  = container.querySelector('[data-vs-empty-message]');
  const emptyCta        = container.querySelector('[data-vs-empty-cta]');
  const resultsEl       = container.querySelector('[data-vs-results]');
  const groupsEl        = container.querySelector('[data-vs-results-groups]');
  const groupTemplate   = container.querySelector('[data-vs-group-template]');
  const dropzone        = container.querySelector('[data-vs-dropzone]');
  const priceWrap       = container.querySelector('[data-vs-price]');
  const priceMinInput   = container.querySelector('[data-vs-price-min]');
  const priceMaxInput   = container.querySelector('[data-vs-price-max]');
  const priceMinLabel   = container.querySelector('[data-vs-price-min-label]');
  const priceMaxLabel   = container.querySelector('[data-vs-price-max-label]');
  const metalButtons    = container.querySelectorAll('[data-vs-metal-option]');
  const categorySelect  = container.querySelector('[data-vs-category]');
  const categoryError   = container.querySelector('[data-vs-category-error]');
  const PRICE_MAX       = Number(priceMaxInput?.max) || 300000;

  // Page-scoped: only the ready-to-ship collection sets this (driven by Liquid).
  const READY_TO_SHIP_FIRST = container.dataset.readyFirst === 'true';
  // Best-guess category hint derived from the current product/collection page.
  const PAGE_CONTEXT        = (container.dataset.context || '').toLowerCase();

  // ─── Dynamic categories — fetched from the backend, never hardcoded ───
  // Last-resort fallback ONLY if the endpoint is unreachable, so search still works.
  const FALLBACK_CATEGORIES = [
    { value: 'ring', label: 'Ring' },
    { value: 'earring', label: 'Earring' },
  ];

  // Backend categories we never want to surface in the dropdown (e.g. test/junk).
  const EXCLUDED_CATEGORIES = new Set(['ring_text']);

  const titleCaseCategory = (slug) =>
    String(slug).replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()).trim();

  // Accept several plausible response shapes: ["ring",…], {categories:[…]},
  // {data:[…]}, or arrays of objects ({value|slug|name|category|id, label}).
  const normalizeCategories = (data) => {
    const raw = Array.isArray(data) ? data
      : Array.isArray(data?.categories) ? data.categories
      : Array.isArray(data?.data) ? data.data
      : [];
    const out = [];
    const seen = new Set();
    for (const entry of raw) {
      let value = typeof entry === 'string'
        ? entry
        : (entry?.value ?? entry?.slug ?? entry?.name ?? entry?.category ?? entry?.id);
      if (value == null) continue;
      value = String(value).trim().toLowerCase();
      if (!value || seen.has(value) || EXCLUDED_CATEGORIES.has(value)) continue;
      seen.add(value);
      const label = (entry && typeof entry === 'object' && entry.label)
        ? String(entry.label)
        : titleCaseCategory(value);
      out.push({ value, label });
    }
    return out;
  };

  // Preselect the category whose value appears in this page's context string.
  // Longest value first so "earring" beats the "ring" substring.
  const applyContextCategory = (cats) => {
    if (!categorySelect || !PAGE_CONTEXT) return;
    const match = [...cats]
      .sort((a, b) => b.value.length - a.value.length)
      .find((c) => PAGE_CONTEXT.includes(c.value));
    if (match) {
      categorySelect.value = match.value;
      if (categoryError) categoryError.hidden = true;
    }
  };

  const populateCategories = (cats) => {
    if (!categorySelect) return;
    const current = categorySelect.value;
    // Keep the disabled placeholder (value=""); replace everything else.
    for (const opt of Array.from(categorySelect.options)) {
      if (opt.value !== '') opt.remove();
    }
    for (const { value, label } of cats) {
      const opt = document.createElement('option');
      opt.value = value;
      opt.textContent = label;
      categorySelect.appendChild(opt);
    }
    // Preserve an existing selection; otherwise apply the page-context match.
    if (current && cats.some((c) => c.value === current)) {
      categorySelect.value = current;
    } else {
      applyContextCategory(cats);
    }
  };

  const READY_TO_SHIP_CATEGORIES = new Set(['ring', 'earring']);

  let categoriesLoaded = false;
  async function loadCategories() {
    if (categoriesLoaded) return;
    categoriesLoaded = true; // don't refetch once we have a good list
    try {
      const res = await fetch(CATEGORIES_ENDPOINT, { signal: timeoutSignal(INIT_TIMEOUT_MS) });
      if (!res.ok) throw new Error(`Categories HTTP ${res.status}`);
      let cats = normalizeCategories(await res.json());
      if (!cats.length) throw new Error('Categories response empty/unrecognized');
      if (READY_TO_SHIP_FIRST) cats = cats.filter((c) => READY_TO_SHIP_CATEGORIES.has(c.value));
      populateCategories(cats);
      vsConsole.log(`[visual-search] loaded ${cats.length} categories`);
    } catch (err) {
      vsConsole.warn('[visual-search] category fetch failed, using fallback:', err.message);
      const fallback = READY_TO_SHIP_FIRST ? FALLBACK_CATEGORIES.filter((c) => READY_TO_SHIP_CATEGORIES.has(c.value)) : FALLBACK_CATEGORIES;
      populateCategories(fallback);
      categoriesLoaded = false; // let a later open retry the live list
    }
  }

  let selectedFile           = null;
  let activeUploadController = null;
  let activeSearchController = null;
  let uploadPromise          = null;
  let uploadFailure          = null; // last upload error, replayed if Search is clicked after it
  let uploadedBlueprint      = null;
  let uploadedMime           = null;
  let lastError              = null;
  let lastUploadError        = null;

  // ─── Generic helpers ───
  const combineSignals = (...signals) => {
    const filtered = signals.filter(Boolean);
    if (filtered.length === 0) return undefined;
    if (filtered.length === 1) return filtered[0];
    if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.any === 'function') {
      return AbortSignal.any(filtered);
    }
    const ctrl = new AbortController();
    for (const s of filtered) {
      if (s.aborted) { ctrl.abort(s.reason); break; }
      s.addEventListener('abort', () => ctrl.abort(s.reason), { once: true });
    }
    return ctrl.signal;
  };

  const timeoutSignal = (ms) => {
    if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
      return AbortSignal.timeout(ms);
    }
    const ctrl = new AbortController();
    setTimeout(() => ctrl.abort(new DOMException('Timed out', 'TimeoutError')), ms);
    return ctrl.signal;
  };

  const withRetry = async (fn, { maxAttempts = 2, isRetryable, baseDelayMs = RETRY_BACKOFF_MS, signal } = {}) => {
    let lastErr;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        return await fn(attempt);
      } catch (err) {
        lastErr = err;
        if (err.name === 'AbortError') throw err;
        if (attempt === maxAttempts) break;
        if (isRetryable && !isRetryable(err)) break;
        const delay = baseDelayMs * attempt;
        vsConsole.log(`[visual-search] retrying after ${delay}ms (attempt ${attempt + 1}/${maxAttempts}) due to:`, err.message);
        await new Promise((resolve) => {
          const t = setTimeout(resolve, delay);
          signal?.addEventListener('abort', () => { clearTimeout(t); resolve(); }, { once: true });
        });
        if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      }
    }
    throw lastErr;
  };

  // Show/hide the loading region and keep aria-busy in step with it, so assistive
  // tech announces the wait instead of reading a silently stale panel.
  const setLoadingVisible = (visible) => {
    if (!loadingEl) return;
    loadingEl.hidden = !visible;
    loadingEl.setAttribute('aria-busy', visible ? 'true' : 'false');
  };

  // ─── Error UI helpers ───
  const showError = (message, { retry } = {}) => {
    lastError = { message, retry: retry || null };
    if (errorMessageEl) errorMessageEl.textContent = message;
    if (errorRetryBtn) errorRetryBtn.hidden = !retry;
    errorEl.hidden = false;
    vsConsole.warn('[visual-search] error shown:', message);
  };

  const hideError = () => {
    lastError = null;
    errorEl.hidden = true;
    if (errorRetryBtn) errorRetryBtn.hidden = true;
  };

  // ─── Empty / unavailable state — friendlier than a bare error, with a CTA ───
  // Point the CTA at the locale-aware make-to-order collection.
  if (emptyCta) emptyCta.href = `${ROOT_URL}/collections/all-1`;

  const showEmpty = () => {
    hideError();
    setLoadingVisible(false);
    clearGroups();                           // drop any stale results
    if (resultsEl) resultsEl.hidden = true;  // hide the "Visually Similar Results" heading
    const catLabel = categorySelect?.selectedOptions?.[0]?.textContent?.trim();
    const subject = catLabel && categorySelect.value ? catLabel : 'This category';
    if (emptyMessageEl) {
      emptyMessageEl.textContent =
        `${subject} is not available right now. Explore our make to order collection for more options`;
    }
    if (emptyEl) {
      emptyEl.hidden = false;
      setTimeout(() => emptyEl.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }
  };

  const hideEmpty = () => {
    if (emptyEl) emptyEl.hidden = true;
    if (resultsEl) resultsEl.hidden = false;
  };

  // ─── Classify errors into user-facing messages ───
  const classifyError = (err, stage) => {
    if (err instanceof TypeError && /fetch|network|load/i.test(err.message)) {
      return { message: 'Network problem. Please check your connection and try again.', retryable: true };
    }
    if (err.name === 'TimeoutError') {
      return { message: stage === 'ai' ? 'Search took too long. Please try again.' : 'Request timed out. Please try again.', retryable: true };
    }
    if (err.status === 429 || /\b429\b/.test(err.message || '')) {
      return { message: 'Too many requests. Please wait a moment and try again.', retryable: true };
    }
    if (err.status === 503 || /\b503\b/.test(err.message || '')) {
      return { message: 'Search service is temporarily unavailable. Please try again in a moment.', retryable: true };
    }
    if (err.status === 413 || /EntityTooLarge/i.test(err.message || '')) {
      return { message: 'Image too large. Please choose one under 30 MB.', retryable: false };
    }
    if (err.status === 415 || /Content-Type/i.test(err.message || '')) {
      return { message: 'Unsupported format. Please upload a JPG, PNG, or WebP image.', retryable: false };
    }
    // 401 after the automatic fresh-slot retry has already been spent.
    if (err.status === 401) {
      return { message: 'Your search session expired. Please try again.', retryable: true };
    }
    // 404 = the object was never stored; retrying re-uploads it.
    if (err.status === 404) {
      return { message: "We couldn't find your uploaded image. Please try again.", retryable: true };
    }
    if (/SignatureDoesNotMatch/i.test(err.message || '')) {
      return { message: 'Upload signature was rejected. Please pick the image again.', retryable: false };
    }
    if (/Policy expired|RequestExpired|ExpiredToken/i.test(err.message || '')) {
      return { message: "Upload link expired. We'll re-upload your image now.", retryable: true };
    }
    if (err.status >= 500) {
      return { message: 'Server error. Please try again.', retryable: true };
    }
    if (err.status >= 400) {
      return { message: stage === 'ai' ? 'Could not analyse this image. Please try another.' : 'Upload was rejected. Please try a different image.', retryable: false };
    }
    return { message: 'Something went wrong. Please try again.', retryable: true };
  };

  // ─── STEP 1: Initialize — fetch the upload slot from our Lambda ───
  // The Lambda returns a presigned S3 POST (a signed policy that enforces size
  // and content-type at upload time), the object's `upload_key`, and a
  // short-lived `search_token`. No AWS-signed URL ever reaches this code, and
  // the key we later hand to /search-by-url carries no credentials.
  // The shopper's own filename is deliberately not sent — S3 names the object.
  const getUploadBlueprint = async (file, mimeType, signal) => {
    const params = new URLSearchParams({ content_type: mimeType });
    const fetchSignal = combineSignals(signal, timeoutSignal(INIT_TIMEOUT_MS));
    const res = await fetch(`${UPLOAD_URL_ENDPOINT}?${params}`, { signal: fetchSignal });
    if (!res.ok) {
      const body = await res.text();
      const err = new Error(`Initialize HTTP ${res.status}: ${body.slice(0, 200)}`);
      err.status = res.status;
      throw err;
    }
    const data = await res.json();
    // Single contract, no legacy fallback: an old-shape response is a deploy
    // mismatch and must fail loudly rather than silently leaking a signed URL.
    if (!data.post_url || !data.post_fields || !data.upload_key) {
      throw new Error(`Blueprint missing post_url/post_fields/upload_key. Got: ${Object.keys(data).join(', ')}`);
    }
    return {
      url: data.post_url,
      method: 'POST',
      fields: data.post_fields,
      // Sent to /search-by-url verbatim — never parsed, trimmed, or rebuilt.
      uploadKey: data.upload_key,
      searchToken: data.search_token ?? null,
    };
  };

  // ─── STEP 2: Upload — presigned S3 POST, with progress + abort + stall watchdog ───
  // XHR rather than fetch() so the progress bar keeps working. The browser sets
  // the multipart Content-Type (with its boundary) itself — setting it by hand
  // breaks the signature, so no headers are attached here.
  const uploadToCloud = (file, blueprint, onProgress, signal) => new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let stallTimer = null;
    let lastLoaded = 0;
    let lastProgressAt = Date.now();

    const cleanup = () => {
      if (stallTimer) { clearInterval(stallTimer); stallTimer = null; }
    };
    const failWith = (error) => { cleanup(); reject(error); };

    xhr.open(blueprint.method, blueprint.url);
    if (signal) {
      if (signal.aborted) {
        const err = new Error('Upload aborted');
        err.name = 'AbortError';
        return reject(err);
      }
      signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      if (e.loaded > lastLoaded) {
        lastLoaded = e.loaded;
        lastProgressAt = Date.now();
      }
    };

    stallTimer = setInterval(() => {
      if (Date.now() - lastProgressAt > UPLOAD_STALL_MS) {
        xhr.abort();
        const err = new Error(`Upload stalled — no progress for ${UPLOAD_STALL_MS}ms`);
        err.name = 'TimeoutError';
        failWith(err);
      }
    }, 5000);

    xhr.onload = () => {
      cleanup();
      if (xhr.status >= 200 && xhr.status < 300) return resolve();
      const body = xhr.responseText || '';
      const err = new Error(`Upload HTTP ${xhr.status}: ${body.slice(0, 200)}`);
      err.status = xhr.status;
      err.responseBody = body;
      reject(err);
    };
    xhr.onerror = () => failWith(new TypeError('Network error during upload'));
    xhr.onabort = () => {
      cleanup();
      const err = new Error('Upload aborted');
      err.name = 'AbortError';
      reject(err);
    };

    // Policy fields first, the file last — S3 ignores anything after `file`.
    const form = new FormData();
    for (const [name, value] of Object.entries(blueprint.fields ?? {})) {
      form.append(name, value);
    }
    form.append('file', file);
    xhr.send(form);
  });

  // ─── STEP 3: Search — call AI pipeline with the uploaded object's key ───
  // The backend reads the object from storage with its own credentials, so we
  // send only the opaque key plus the token that authorises this search.
  const searchByImage = async (blueprint, params, signal) => {
    const fetchSignal = combineSignals(signal, timeoutSignal(AI_TIMEOUT_MS));
    const res = await fetch(AI_SEARCH_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // Key and token last: a filter key can never shadow either of them.
      body: JSON.stringify({
        ...params,
        upload_key: blueprint.uploadKey,
        ...(blueprint.searchToken ? { search_token: blueprint.searchToken } : {}),
      }),
      signal: fetchSignal,
    });
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      const err = new Error(`AI HTTP ${res.status}: ${body.slice(0, 200)}`);
      err.status = res.status;
      err.responseBody = body;
      // 401 bodies carry {detail:{error:"missing"|"invalid"|"expired"}} — keep
      // the code so the caller can tell a stale token from a wrong one.
      try {
        const detail = JSON.parse(body)?.detail;
        if (detail && typeof detail === 'object') err.tokenError = detail.error ?? null;
      } catch { /* non-JSON error body — nothing to extract */ }
      throw err;
    }
    try {
      const data = await res.json();
      vsConsole.log('[visual-search] search response:', data);
      return data;
    } catch {
      throw new Error('AI pipeline returned malformed JSON');
    }
  };

  // ─── UI helpers ───
  const setLoadingMessage = (text) => {
    if (loadingMessage) loadingMessage.textContent = text;
  };

  const setProgress = (percent) => {
    progressEl.hidden = false;
    progressEl.setAttribute('aria-valuenow', String(percent));
    progressBar.style.setProperty('--vs-progress', `${percent}%`);
  };

  const resetProgress = () => {
    progressEl.hidden = true;
    progressEl.setAttribute('aria-valuenow', '0');
    progressBar.style.setProperty('--vs-progress', '0%');
  };

  // ─── Price range (dual-thumb) ───
  const formatPrice = (v) => {
    if (v <= 0) return '₹0';
    if (v < 100000) return `₹${v.toLocaleString('en-IN')}`;
    return `₹${(v / 100000).toFixed(1)} L`;
  };

  const updatePriceSlider = () => {
    if (!priceMinInput || !priceMaxInput) return;
    const lo = Number(priceMinInput.value);
    const hi = Number(priceMaxInput.value);
    const loPct = (lo / PRICE_MAX) * 100;
    const hiPct = (hi / PRICE_MAX) * 100;
    priceWrap.style.setProperty('--vs-fill-start', `${loPct}%`);
    priceWrap.style.setProperty('--vs-fill-end', `${hiPct}%`);
    if (priceMinLabel) priceMinLabel.textContent = formatPrice(lo);
    if (priceMaxLabel) priceMaxLabel.textContent = formatPrice(hi);
    // Announce the formatted amount instead of the raw step value.
    priceMinInput.setAttribute('aria-valuetext', formatPrice(lo));
    priceMaxInput.setAttribute('aria-valuetext', formatPrice(hi));
  };

  // ─── Background upload: STEPS 1 + 2 — runs the moment a file is picked ───
  const uploadInBackground = async (file, mimeType) => {
    activeUploadController = new AbortController();
    const { signal } = activeUploadController;

    setLoadingVisible(true);
    resetProgress();
    setLoadingMessage('Preparing upload…');
    hideError();

    const isInitRetryable = (err) =>
      err instanceof TypeError ||
      err.name === 'TimeoutError' ||
      err.status === 429 ||
      err.status === 503 ||
      (err.status >= 500 && err.status < 600);

    // S3 answers a POST-policy violation with 400/403 too, so only an expired
    // policy is retried — a file that breaks the size/type rules would fail
    // identically on every attempt and must surface to the shopper instead.
    const isUploadRetryable = (err) =>
      err instanceof TypeError ||
      err.name === 'TimeoutError' ||
      (err.status >= 500 && err.status < 600) ||
      /Policy expired|RequestExpired|ExpiredToken/i.test(err.message || '');

    try {
      vsConsole.log('[visual-search] ── STEP 1: initialize');
      const blueprint = await withRetry(
        () => getUploadBlueprint(file, mimeType, signal),
        { maxAttempts: 2, isRetryable: isInitRetryable, signal },
      );
      vsConsole.log('[visual-search] blueprint:', {
        url: blueprint.url,
        method: blueprint.method,
        uploadKey: blueprint.uploadKey,
        hasToken: Boolean(blueprint.searchToken),
      });
      if (signal.aborted) throw new DOMException('Aborted', 'AbortError');

      vsConsole.log('[visual-search] ── STEP 2: upload');
      setLoadingMessage('Uploading 0%…');
      setProgress(0);

      let currentBlueprint = blueprint;
      await withRetry(
        async (attempt) => {
          // A slot is single-use and short-lived: always take a fresh one on a
          // retry rather than re-posting against a policy that may have lapsed.
          if (attempt > 1) {
            vsConsole.log('[visual-search] retrying upload with a fresh slot:', lastUploadError?.message);
            currentBlueprint = await getUploadBlueprint(file, mimeType, signal);
          }
          try {
            await uploadToCloud(
              file,
              currentBlueprint,
              (pct) => {
                setProgress(pct);
                setLoadingMessage(`Uploading ${pct}%…`);
              },
              signal,
            );
          } catch (err) {
            lastUploadError = err;
            throw err;
          }
        },
        { maxAttempts: 2, isRetryable: isUploadRetryable, signal },
      );

      uploadedBlueprint = currentBlueprint;
      uploadFailure = null;
      vsConsole.log('[visual-search] upload completed');
      return currentBlueprint;
    } catch (err) {
      uploadedBlueprint = null;
      if (err.name === 'AbortError') {
        vsConsole.log('[visual-search] upload aborted');
      } else {
        vsConsole.error('[visual-search] upload FAILED:', err);
        const { message, retryable } = classifyError(err, 'upload');
        uploadFailure = { message, retryable };
        showError(message, {
          retry: retryable && selectedFile
            ? () => startUpload(selectedFile, uploadedMime || selectedFile.type)
            : null,
        });
      }
      throw err;
    } finally {
      setLoadingVisible(false);
      resetProgress();
      activeUploadController = null;
    }
  };

  // uploadInBackground already reports its own failures through the error UI,
  // and every caller re-checks `uploadedBlueprint` before using the result — so
  // the stored promise settles instead of rejecting, which keeps a failed
  // background upload from surfacing as an unhandled rejection in the console.
  const startUpload = (file, mimeType) => {
    uploadPromise = uploadInBackground(file, mimeType).catch(() => null);
    return uploadPromise;
  };

  // ─── Search click: STEP 3 only ───
  const handleSearch = async () => {
    if (!selectedFile) return;

    // Category is required — show an inline message and stop if none picked.
    if (!categorySelect?.value) {
      if (categoryError) categoryError.hidden = false;
      categorySelect?.setAttribute('aria-invalid', 'true');
      categorySelect?.focus();
      return;
    }
    if (categoryError) categoryError.hidden = true;
    categorySelect.removeAttribute('aria-invalid');

    hideError();
    hideEmpty();

    activeSearchController?.abort();
    activeSearchController = new AbortController();
    const { signal } = activeSearchController;

    setLoadingVisible(true);
    searchBtn.disabled = true;
    searchBtn.classList.add('is-loading');

    let coldStartTimer = null;

    try {
      if (!uploadedBlueprint) {
        if (!uploadPromise) {
          showError('Please pick an image first.');
          return;
        }
        setLoadingMessage('Waiting for upload to finish…');
        await uploadPromise; // settles either way; the blueprint check below decides
        if (signal.aborted) return;
      }

      if (!uploadedBlueprint) {
        // The upload failed. Its message was cleared when this search started,
        // so put it back rather than leaving the shopper with a dead button.
        if (!signal.aborted) {
          showError(uploadFailure?.message || "We couldn't upload your image. Please try again.", {
            retry: selectedFile
              ? () => startUpload(selectedFile, uploadedMime || selectedFile.type)
              : null,
          });
        }
        return;
      }

      vsConsole.log('[visual-search] ── STEP 3: search');
      setLoadingMessage('Analysing your image…');

      coldStartTimer = setTimeout(() => {
        setLoadingMessage('Analysing your image…');
      }, COLD_START_HINT_MS);

      const category = categorySelect.value; // validated as non-empty above
      const topK = TOP_K;

      // Build the request payload. Optional filters are omitted when blank so
      // the backend treats them as "no filter".
      const searchParams = { category, top_k: topK };
      const priceMin = Number(priceMinInput?.value) || 0;
      const priceMax = Number(priceMaxInput?.value);
      if (priceMin > 0) searchParams.price_min = priceMin;            // omit at full range
      if (priceMax < PRICE_MAX) searchParams.price_max = priceMax;    // omit at full range
      const metals = Array.from(metalButtons)
        .filter((b) => b.getAttribute('aria-pressed') === 'true')
        .map((b) => b.value);
      if (metals.length) searchParams.metal = metals;                 // e.g. ["white","rose"]
      // Page-scoped flag: float ready-to-ship matches to the top only on the
      // ready-to-ship collection page; sent explicitly as false everywhere else.
      searchParams.ready_to_ship_first = READY_TO_SHIP_FIRST;

      const runSearch = async (blueprint) => {
        const isAiRetryable = (err) =>
          err instanceof TypeError ||
          err.name === 'TimeoutError' ||
          err.status === 429 ||
          err.status === 503 ||
          (err.status >= 500 && err.status < 600);
        return withRetry(
          () => searchByImage(blueprint, searchParams, signal),
          { maxAttempts: 2, isRetryable: isAiRetryable, signal },
        );
      };

      // A stale token (401) or an object the backend can't find (404) is only
      // recoverable by taking a NEW slot and re-uploading: a fresh slot names a
      // new, still-empty key, so reusing the old key or token would just fail
      // again. The file is still in memory, so the shopper re-picks nothing.
      const needsFreshUpload = (err) =>
        (err.status === 401 && err.tokenError !== 'missing') || err.status === 404;

      let aiData = null;
      try {
        aiData = await runSearch(uploadedBlueprint);
      } catch (err) {
        if (err.name === 'AbortError') return;

        if (needsFreshUpload(err) && selectedFile) {
          vsConsole.log(`[visual-search] search rejected (${err.status}${err.tokenError ? `/${err.tokenError}` : ''}) — re-uploading with a fresh slot`);
          setLoadingMessage('Refreshing your upload…');
          try {
            uploadedBlueprint = null;
            await startUpload(selectedFile, uploadedMime || selectedFile.type);
            if (uploadedBlueprint) {
              aiData = await runSearch(uploadedBlueprint); // once only — no loop
            }
          } catch (retryErr) {
            if (retryErr.name !== 'AbortError') {
              const { message, retryable } = classifyError(retryErr, 'ai');
              showError(message, { retry: retryable ? () => handleSearch() : null });
              return;
            }
          }
        } else {
          vsConsole.warn('[visual-search] AI call failed:', err.message);
          const { message, retryable } = classifyError(err, 'ai');
          showError(message, { retry: retryable ? () => handleSearch() : null });
          return;
        }
      }

      vsConsole.log('[visual-search] AI response:', aiData);

      // The backend detects one or more objects in the photo and returns a
      // ranked list per object. Every item_index gets its own group — the
      // lists are shown side by side, never merged into one ranking.
      const aiItems = (Array.isArray(aiData?.items) ? aiData.items : [])
        .filter((item) => item?.results?.length > 0 || item?.skus?.length > 0)
        .sort((a, b) => (a.item_index ?? Infinity) - (b.item_index ?? Infinity));

      if (aiItems.length === 0) {
        showEmpty();
      } else {
        vsConsole.log(`[visual-search] rendering ${aiItems.length} detected item(s)`);
        await renderAIItems(aiItems);
      }
    } finally {
      if (coldStartTimer) clearTimeout(coldStartTimer);
      setLoadingVisible(false);
      searchBtn.classList.remove('is-loading');
      searchBtn.disabled = !selectedFile;
      activeSearchController = null;
    }
  };

  // ─── File handling ───
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) processFile(file);
  };

  const processFile = (file) => {
    hideError();
    hideEmpty();

    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      showError('Unsupported format. Please upload a JPG, PNG, or WebP image.');
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      showError('Image too large. Maximum size is 30 MB.');
      return;
    }
    if (file.size === 0) {
      showError('This file appears to be empty. Please choose another.');
      return;
    }

    const mimeType = file.type;

    activeUploadController?.abort();
    activeSearchController?.abort();
    uploadedBlueprint = null;
    uploadedMime = mimeType;
    uploadPromise = null;
    uploadFailure = null;

    selectedFile = file;

    const reader = new FileReader();
    reader.onload = (e) => {
      previewImg.src = e.target.result;
      preview.hidden = false;
      uploadText.textContent = 'Change';
      searchBtn.disabled = false;
    };
    reader.onerror = () => {
      showError('Could not read this image file. Please pick another.', {
        retry: () => fileInput.click(),
      });
      selectedFile = null;
      searchBtn.disabled = true;
    };
    reader.readAsDataURL(file);

    startUpload(file, mimeType);
  };

  const handleRemove = (e) => {
    e.preventDefault();
    e.stopPropagation();
    activeUploadController?.abort();
    activeSearchController?.abort();
    uploadedBlueprint = null;
    uploadedMime = null;
    uploadPromise = null;
    uploadFailure = null;
    selectedFile = null;
    fileInput.value = '';
    previewImg.src = '';
    preview.hidden = true;
    uploadText.textContent = 'Upload';
    searchBtn.disabled = true;
    hideError();
    hideEmpty();
  };

  // ─── Result rendering ───
  // Extract the product handle from a rendered theme product-card element.
  const handleFromCard = (cardEl) => {
    const link = cardEl.querySelector('a[href*="/products/"]');
    const match = link && link.getAttribute('href').match(/\/products\/([^/?#]+)/);
    if (match) return decodeURIComponent(match[1]);
    return cardEl.querySelector('[data-product-handle]')?.dataset.productHandle || null;
  };

  // Run ONE storefront search (theme's recently-viewed view, which renders a
  // <product-card> per result) and return the cards keyed by product handle.
  const searchCards = async (query, signal) => {
    const url = `${SEARCH_URL}?view=recently-viewed&type=product&q=${encodeURIComponent(query)}`;
    const res = await fetch(url, { signal });
    if (!res.ok) throw new Error(`Card render HTTP ${res.status}`);
    const html = await res.text();
    const doc = document.createElement('div');
    doc.innerHTML = html;

    const byHandle = new Map();
    for (const cardEl of doc.querySelectorAll('product-card, .product-card')) {
      const handle = handleFromCard(cardEl);
      if (handle && !byHandle.has(handle)) byHandle.set(handle, cardEl);
    }
    return byHandle;
  };

  // Format a Shopify money amount (minor units, e.g. paise) as ₹ with Indian grouping.
  const formatMoney = (cents) => `₹${(Math.round(Number(cents) || 0) / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

  // Normalize a Shopify image URL (protocol-relative → https) and size it down.
  const sizedImage = (src) => {
    if (!src) return null;
    let url = String(src);
    if (url.startsWith('//')) url = `https:${url}`;
    return url + (url.includes('?') ? '&' : '?') + 'width=400';
  };

  // Build a result card from product JSON, pinned to the matched variant.
  const buildCard = (item, product, variant) => {
    const href = `${product.url}${variant ? `?variant=${variant.id}` : ''}`;
    const imgSrc = sizedImage(
      (variant && variant.featured_image && variant.featured_image.src) ||
      product.featured_image ||
      (product.images && product.images[0]),
    );
    const price = variant ? variant.price : product.price;

    const card = document.createElement('a');
    card.className = 'vs-card';
    card.href = href;
    card.title = product.title;
    card.target = '_blank';                                                                                          
    card.rel = 'noopener noreferrer';

    const figure = document.createElement('div');
    figure.className = 'vs-card-image';
    if (imgSrc) {
      const img = document.createElement('img');
      img.src = imgSrc;
      img.alt = product.title;
      img.loading = 'lazy';
      img.width = 400;
      img.height = 400;
      figure.appendChild(img);
    }
    if (item.ready_to_ship === true) {
      const ship = document.createElement('span');
      ship.className = 'vs-card-ship';
      ship.textContent = 'Ready To Ship';
      figure.appendChild(ship);
    }
    card.appendChild(figure);

    const info = document.createElement('div');
    info.className = 'vs-card-info';
    const title = document.createElement('h3');
    title.className = 'vs-card-title';
    title.textContent = product.title;
    const priceEl = document.createElement('p');
    priceEl.className = 'vs-card-price';
    priceEl.textContent = formatMoney(price);
    info.append(title, priceEl);
    card.appendChild(info);

    return card;
  };

  // Resolve one AI result to a card by fetching the product directly by its
  // product_handle (exact + reliable — storefront search can't surface
  // duplicate/odd-named products). Picks the variant matching the SKU so the
  // right colour shows. Returns null (→ skipped) only if the product 404s.

  // Fetch a product by handle and build its card. With requireSku, only returns
  // a card if one of its variants matches item.sku (used to verify candidates).
  const fetchProductCard = async (handle, item, signal, requireSku) => {
    let product;
    try {
      const res = await fetch(`${ROOT_URL}/products/${encodeURIComponent(handle)}.js`, { signal });
      if (!res.ok) return null; // 404 / unpublished
      product = await res.json();
    } catch (err) {
      if (err.name === 'AbortError') throw err;
      return null;
    }
    const variant = (product.variants || []).find((v) => v.sku === item.sku) || null;
    if (requireSku && !variant) return null; // candidate doesn't carry our SKU → reject
    return buildCard(item, product, variant);
  };

  const resolveResultCard = async (item, signal) => {
    // Fast path: the product_handle resolves directly.
    if (item.product_handle) {
      const card = await fetchProductCard(item.product_handle, item, signal, false);
      if (card) return card;
    }
    // Fallback: the handle 404'd (AI slug ≠ Shopify slug). Find the product by
    // SKU — search the storefront, then VERIFY each candidate by fetching its
    // product JSON and confirming a variant SKU matches. Never an unverified hit.
    if (item.sku) {
      const tried = new Set();
      for (const query of [item.name, item.sku].filter(Boolean)) {
        let candidates;
        try {
          candidates = await searchCards(query, signal);
        } catch (err) {
          if (err.name === 'AbortError') throw err;
          continue;
        }
        for (const handle of candidates.keys()) {
          if (tried.has(handle)) continue;
          tried.add(handle);
          const card = await fetchProductCard(handle, item, signal, true);
          if (card) return card;
          if (tried.size >= 8) break; // cap fetches per result
        }
        if (tried.size >= 8) break;
      }
    }
    return null;
  };

  // Wrap a theme card and append it to the given item's grid.
  const appendCard = (cardEl, gridEl) => {
    const wrapper = document.createElement('div');
    wrapper.className = 'vs-result-card';
    wrapper.appendChild(cardEl);
    gridEl.appendChild(wrapper);
  };

  // ─── Per-item result groups (one per detected object / item_index) ───
  function clearGroups() {
    if (groupsEl) groupsEl.innerHTML = '';
  }

  // Clone the Liquid <template> for one detected item and return its parts.
  // `position` is the 1-based display order, not the raw item_index.
  function createGroup(item, position) {
    const fragment = groupTemplate.content.cloneNode(true);
    const groupEl = fragment.querySelector('[data-vs-group]');
    const titleEl = fragment.querySelector('[data-vs-group-title]');
    const gridEl = fragment.querySelector('[data-vs-grid]');

    groupEl.dataset.itemIndex = String(item.item_index ?? position - 1);
    // Numbered only — the category is already the one the shopper picked, so
    // repeating it on every group adds nothing.
    titleEl.textContent = `Item ${position}`;

    groupsEl.appendChild(fragment);
    return { groupEl, gridEl };
  }

  // Render every detected item into its own group, in item_index order. Groups
  // resolve one at a time so the first list paints before the next starts
  // fetching, and so we never fire hundreds of product requests at once.
  async function renderAIItems(items) {
    clearGroups();
    if (resultsEl) resultsEl.hidden = false;
    // A single detection keeps the original layout — no per-item header.
    groupsEl.classList.toggle('vs-results-groups--single', items.length === 1);

    let scrolled = false;
    let totalRendered = 0;

    for (const [i, item] of items.entries()) {
      const { groupEl, gridEl } = createGroup(item, i + 1);

      let rendered = 0;
      if (item.results?.length > 0) {
        rendered = await renderAIResults(item.results, gridEl);
      } else if (item.skus?.length > 0) {
        rendered = await renderAIResultsFromSkus(item.skus, gridEl);
      }
      if (rendered === 0) {
        groupEl.remove(); // nothing resolved for this object — drop the empty group
        if (activeSearchController?.signal.aborted) return; // a newer search owns the UI now
        continue;
      }
      totalRendered += rendered;

      if (!scrolled) {
        scrolled = true;
        setTimeout(() => resultsEl.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      }
    }

    vsConsole.log(`[visual-search] rendered ${totalRendered} cards across ${groupsEl.children.length} item group(s)`);
    // Don't paper over a card-render error with the friendlier empty state.
    if (totalRendered === 0 && !lastError) showEmpty();
  }

  // Primary path: AI returns ranked products with `product_handle`/`sku` + score.
  // Resolve each by handle (then SKU) and render in the AI's order with a match %.
  // A result that resolves to no storefront product is silently skipped.
  // Renders into one item's grid and returns how many cards it produced.
  async function renderAIResults(results, gridEl) {
    // Keep anything we can resolve, but drop non-active/draft products. Status is
    // only dropped when explicitly non-ACTIVE (empty/missing status is kept).
    const isActive = (item) => !item.status || String(item.status).toUpperCase() === 'ACTIVE';
    const items = results.filter((item) => (item.product_handle || item.sku) && isActive(item));

    // Order purely by the backend's rank (lower rank = better match); items with
    // no rank sink to the bottom. No ready-to-ship priority. Array.sort is stable.
    items.sort((a, b) => (a.rank ?? Infinity) - (b.rank ?? Infinity));

    if (items.length === 0) return 0;

    const signal = activeSearchController?.signal;

    let cards;
    try {
      cards = await Promise.all(
        items.map((item) =>
          resolveResultCard(item, signal).catch((err) => {
            if (err.name === 'AbortError') throw err;
            return null; // one missing result shouldn't sink the whole grid
          }),
        ),
      );
    } catch (err) {
      if (err.name === 'AbortError') return 0;
      vsConsole.error('[visual-search] failed to render theme cards:', err);
      showError('Could not load product cards. Please try again.', { retry: () => handleSearch() });
      return 0;
    }

    // Show every result we could resolve — duplicates included (no dedupe).
    let rendered = 0;
    items.forEach((item, i) => {
      const cardEl = cards[i];
      if (!cardEl) return; // not found on storefront → skip
      appendCard(cardEl, gridEl);
      rendered += 1;
    });

    vsConsole.log(`[visual-search] rendered ${rendered}/${items.length} theme product cards`);
    return rendered;
  }

  // Fallback: AI returned bare SKUs (no product_handle). Search the storefront
  // once per SKU and take its top card — again per-SKU, not one combined query.
  // Renders into one item's grid and returns how many cards it produced.
  async function renderAIResultsFromSkus(skus, gridEl) {
    vsConsole.log(`[visual-search] API returned ${skus.length} SKUs:`, skus);

    const signal = activeSearchController?.signal;
    let cardsPerSku;
    try {
      cardsPerSku = await Promise.all(
        skus.map((sku) =>
          searchCards(sku, signal)
            .then((byHandle) => byHandle.values().next().value ?? null)
            .catch((err) => {
              if (err.name === 'AbortError') throw err;
              return null; // one SKU failing shouldn't sink the whole grid
            }),
        ),
      );
    } catch (err) {
      if (err.name === 'AbortError') return 0;
      vsConsole.error('[visual-search] failed to render theme cards (skus):', err);
      showError('Could not load product cards. Please try again.', { retry: () => handleSearch() });
      return 0;
    }

    const seen = new Set();
    const renderedSkus = [];
    skus.forEach((sku, i) => {
      const cardEl = cardsPerSku[i];
      if (!cardEl) return;
      const handle = handleFromCard(cardEl);
      if (handle && seen.has(handle)) return; // same product matched by 2 SKUs
      if (handle) seen.add(handle);
      appendCard(cardEl, gridEl);
      renderedSkus.push(sku);
    });

    vsConsole.log(`[visual-search] rendered ${renderedSkus.length}/${skus.length} theme product cards (skus):`, renderedSkus);
    return renderedSkus.length;
  }

  // ─── Bind events ───
  fileInput.addEventListener('change', handleFileSelect);
  removeBtn.addEventListener('click', handleRemove);
  searchBtn.addEventListener('click', handleSearch);
  categorySelect?.addEventListener('change', () => {
    if (!categorySelect.value) return;
    if (categoryError) categoryError.hidden = true;
    categorySelect.removeAttribute('aria-invalid');
  });
  errorDismissBtn?.addEventListener('click', hideError);
  errorRetryBtn?.addEventListener('click', () => {
    const retry = lastError?.retry;
    hideError();
    if (typeof retry === 'function') retry();
  });

  for (const evt of ['dragenter', 'dragover']) {
    dropzone.addEventListener(evt, (e) => {
      e.preventDefault();
      dropzone.classList.add('is-dragover');
    });
  }
  for (const evt of ['dragleave', 'drop']) {
    dropzone.addEventListener(evt, (e) => {
      e.preventDefault();
      dropzone.classList.remove('is-dragover');
    });
  }
  dropzone.addEventListener('drop', (e) => {
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    if (file) processFile(file);
  });

  // Price slider: keep the two thumbs from crossing, then repaint fill + labels.
  if (priceMinInput && priceMaxInput) {
    priceMinInput.addEventListener('input', () => {
      if (Number(priceMinInput.value) > Number(priceMaxInput.value)) {
        priceMinInput.value = priceMaxInput.value;
      }
      updatePriceSlider();
    });
    priceMaxInput.addEventListener('input', () => {
      if (Number(priceMaxInput.value) < Number(priceMinInput.value)) {
        priceMaxInput.value = priceMinInput.value;
      }
      updatePriceSlider();
    });
  }

  // Metal colour: multi-select swatches (toggle each independently).
  metalButtons.forEach((btn) => {
    btn.addEventListener('click', () => {
      // Multi-select: each swatch toggles on its own (white + rose together).
      const active = btn.getAttribute('aria-pressed') === 'true';
      btn.setAttribute('aria-pressed', active ? 'false' : 'true');
    });
  });

  updatePriceSlider();
});
